import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';

// Public account-deletion instructions page. Required by the Google Play
// "Data safety" form — it must be reachable WITHOUT logging in, name the app,
// list the steps to request deletion, and state what is deleted vs retained.
const DeleteAccount = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Delete your EventSawa account | EventSawa</title>
        <meta name="description" content="How to request deletion of your EventSawa account and associated data." />
      </Helmet>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center mb-8">
          <Link to="/" className="flex items-center text-gray-600 hover:text-indigo-600">
            <FiArrowLeft className="w-5 h-5 mr-2" />
            <span>Back to Home</span>
          </Link>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-6">Delete your EventSawa account</h1>

        <div className="space-y-8">
          <div>
            <p className="text-gray-600">
              This page explains how to request deletion of your <strong>EventSawa</strong> account
              (the EventSawa app and website, published by EventSawa) and the data associated with it.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to request deletion</h2>
            <div className="border-l-4 border-indigo-500 pl-4 mb-6">
              <h3 className="font-semibold text-gray-700">Option 1 — In the app or website (recommended)</h3>
              <ol className="list-decimal list-inside text-gray-600 space-y-1 mt-2">
                <li>Sign in to EventSawa.</li>
                <li>Go to <strong>Settings</strong> (from your profile menu).</li>
                <li>Tap <strong>Delete My Account</strong>.</li>
                <li>Confirm and verify the request when prompted.</li>
              </ol>
            </div>

            <div className="border-l-4 border-indigo-500 pl-4">
              <h3 className="font-semibold text-gray-700">Option 2 — By email (if you can't sign in)</h3>
              <p className="text-gray-600 mt-2">
                Send a deletion request from the email address on your account to{' '}
                <a href="mailto:3businessmails@gmail.com" className="text-indigo-600 hover:underline">
                  3businessmails@gmail.com
                </a>{' '}
                with the subject <em>"Delete my EventSawa account"</em>. We will verify your identity
                and process the request.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What happens next</h2>
            <p className="text-gray-600">
              When you request deletion, your account is <strong>scheduled for deletion in 30 days</strong>.
              During this grace period you can sign in and cancel the request to keep your account.
              After 30 days, the account and its personal data are permanently removed.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data that is deleted</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>Your account and profile (name, email, phone number, role).</li>
              <li>Events, guest lists, seating and forms you created.</li>
              <li>Tickets, RSVPs and personal activity tied to your account.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data that may be retained</h2>
            <p className="text-gray-600">
              Certain records may be retained after account deletion where we are legally required to
              keep them — for example, payment and transaction records needed for accounting, tax and
              fraud-prevention purposes. These are kept for the period required by applicable law
              (typically up to 7 years) and are then deleted. Anonymised or aggregated data that no
              longer identifies you may be retained.
            </p>
          </div>

          <div>
            <p className="text-gray-600">
              For more on how we handle your data, see our{' '}
              <Link to="/privacy-policy" className="text-indigo-600 hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
